import { apiClient } from './apiClient';

/**
 * Internal helper for RPC uploads calls
 */
async function rpcUploads<T>(company: string, action: string, payload: any, isFormData = false): Promise<T> {
  const config: any = {
    withCredentials: true,
  };

  if (isFormData) {
    config.headers = { 'Content-Type': 'multipart/form-data' };
    config.timeout = 600000; // 10 minutes timeout for large file uploads
  }

  const { data } = await apiClient.post(`/api/${company}/rpc/uploads/${action}`, payload, config);
  
  if ((data as any)?.ok && 'data' in data) {
    return (data as any).data as T;
  }
  if ((data as any)?.ok) {
    const { ok, ...rest } = data as any;
    return rest as T;
  }
  return data as T;
}

/**
 * List client attachments by folder
 */
export async function listClientAttachments(
  company: string,
  clientId: string,
  folder: string,
  options?: {
    startDate?: string;
    endDate?: string;
    subfolder?: string;
  }
) {
  const result = await rpcUploads<{ attachments: any[] }>(company, 'list-client-attachments', {
    clientId,
    folder,
    ...options,
  });
  return result?.attachments || [];
}

/**
 * List admin client attachments (light listing)
 */
export async function listAdminClientAttachments(
  company: string,
  clientId: string,
  options?: {
    limit?: number;
    fields?: string;
  }
) {
  const result = await rpcUploads<{ files: any[] }>(company, 'list-admin-client-attachments', {
    clientId,
    ...options,
  });
  return result?.files || [];
}

/**
 * Get count of attachments for a client
 */
export async function getClientAttachmentsCount(company: string, clientId: string): Promise<number> {
  const result = await rpcUploads<{ count: number }>(company, 'get-client-attachments-count', { clientId });
  return result?.count || 0;
}

/**
 * Upload a file attachment
 */
export async function uploadFile(
  company: string,
  clientId: string,
  folder: string,
  file: File,
  options?: {
    subfolder?: string;
    title?: string;
    uploaderId?: string;
  }
) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('clientId', clientId);
  formData.append('folder', folder);
  if (options?.subfolder) formData.append('subfolder', options.subfolder);
  if (options?.title) formData.append('title', options.title);
  if (options?.uploaderId) formData.append('uploaderId', options.uploaderId);

  const result = await rpcUploads<any>(company, 'upload-file', formData, true);
  return result;
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(company: string, publicId: string) {
  const result = await rpcUploads<{ success: boolean }>(company, 'delete-attachment', { public_id: publicId });
  return result;
}

/**
 * Send attachments as ZIP via email
 */
export async function sendZip(
  company: string,
  payload: {
    clientId: string;
    folder: string;
    email: string;
    startDate?: string;
    endDate?: string;
    subfolder?: string;
    q?: string;
    message?: string;
    senderName?: string;
  }
) {
  const result = await rpcUploads<{}>(company, 'send-zip', payload);
  return result;
}

/**
 * Download attachments as ZIP
 * Returns the URL for downloading the ZIP file
 */
export function downloadZip(
  company: string,
  params: {
    clientId: string;
    folder: string;
    startDate?: string;
    endDate?: string;
    subfolder?: string;
    q?: string;
  }
): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  return `/api/${company}/rpc/uploads/zip?${queryParams.toString()}`;
}

/**
 * Download attachment securely (returns Blob)
 * Uses GET endpoint for browser compatibility
 */
export async function downloadAttachment(
  company: string,
  publicId: string,
  mode: 'download' | 'view' = 'download'
): Promise<Blob> {
  const { data } = await apiClient.get(`/api/${company}/rpc/uploads/secure`, {
    params: {
      pid: publicId,
      mode,
    },
    responseType: 'blob',
    withCredentials: true,
    timeout: 600000, // 10 minutes timeout for large file downloads
  });
  return data;
}

/**
 * Upload company logo (first time)
 */
export async function uploadLogo(company: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const result = await rpcUploads<{
    url: string;
    public_id: string;
    width: number;
    height: number;
    format: string;
    bytes: number;
  }>(company, 'upload-logo', formData, true);
  return result;
}

/**
 * Update company logo (replace existing)
 */
export async function updateLogo(company: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const result = await rpcUploads<{
    url: string;
    public_id: string;
    width: number;
    height: number;
    format: string;
    bytes: number;
  }>(company, 'update-logo', formData, true);
  return result;
}

/**
 * Get company logo info
 * Returns { hasLogo: false } if no logo exists, or { hasLogo: true, url, ... } if logo exists
 */
export async function getLogo(company: string): Promise<{
  hasLogo: boolean;
  url?: string;
  public_id?: string;
  width?: number;
  height?: number;
  format?: string;
  bytes?: number;
}> {
  try {
    const result = await rpcUploads<{
      hasLogo: boolean;
      url?: string;
      public_id?: string;
      width?: number;
      height?: number;
      format?: string;
      bytes?: number;
    }>(company, 'get-logo', {});
    // Ensure hasLogo is always present
    return {
      hasLogo: result?.hasLogo ?? false,
      ...(result?.hasLogo && result?.url ? {
        url: result.url,
        public_id: result.public_id,
        width: result.width,
        height: result.height,
        format: result.format,
        bytes: result.bytes,
      } : {}),
    };
  } catch (err: any) {
    // If API call fails, treat as "no logo" instead of throwing
    // This prevents console spam and UI errors
    console.warn('[uploadsApi.getLogo] Error fetching logo, treating as no logo:', err?.message);
    return { hasLogo: false };
  }
}

/**
 * Delete company logo
 */
export async function deleteLogo(company: string) {
  const result = await rpcUploads<{ message: string; deleted: boolean }>(company, 'delete-logo', {});
  return result;
}

/**
 * Get invoice subfolders (month-year list)
 */
export async function getInvoicesFolders(company: string, clientId: string): Promise<string[]> {
  const result = await rpcUploads<{ subfolders: string[] }>(company, 'get-invoices-folders', { clientId });
  return result?.subfolders || [];
}

/**
 * Get admin lite info (for public UI)
 */
export async function getAdminLite(company: string, adminId: string) {
  const result = await rpcUploads<{ company: string; adminName: string }>(company, 'get-admin-lite', { adminId });
  return result;
}

/**
 * Upload bug attachment
 */
export async function uploadBugAttachment(
  company: string,
  bugId: string,
  file: File,
  options?: {
    title?: string;
    uploaderId?: string;
  }
) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bugId', bugId);
  if (options?.title) formData.append('title', options.title);
  if (options?.uploaderId) formData.append('uploaderId', options.uploaderId);

  const result = await rpcUploads<any>(company, 'upload-bug-attachment', formData, true);
  return result;
}

